import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { deleteRepairItem, getRepairItems, createRepairItem } from '@/services/rc/repair-items.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { Ref } from 'vue'

/**
 * Composable exporting repair items source values,
 * @param minerId the miner id
 * @param failureId the failure id to which this repair items are bound to
 */
export const useMinerPartRepairItems = (minerId: Ref<number>, failureId: Ref<number>) => {
  const queryClient = useQueryClient()

  // I know, this does not depend on minerId or failureId, but won't be used on
  // another context and IMO makes sense it's bundled to the mutations that use it
  const getRepairItemsQuery = useQuery({
    queryFn: getRepairItems,
    queryKey: ['repair-items'],
  })

  const createRepairItemMutation = useMutation({
    mutationFn: ({ repair_item_id }: { repair_item_id: number }) =>
      createRepairItem(failureId.value, { repair_item_id }),
    onError: NotifyErrorWrapper('Error creating repair item on failure'),
    onSuccess: () => {
      NotifySuccess(`Repair item created successfully`)
      queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] })
    },
  })

  const deleteRepairItemMutation = useMutation({
    mutationFn: ({ repair_item_id }: { repair_item_id: number }) =>
      deleteRepairItem(failureId.value, { repair_item_id }),
    onError: NotifyErrorWrapper('Error deleting repair item on failure'),
    onSuccess: () => {
      NotifySuccess(`Repair item deleted successfully`)
      queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] })
    },
  })

  return {
    getRepairItemsQuery,
    createRepairItemMutation,
    deleteRepairItemMutation,
  }
}
